import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useAuth } from "react-oidc-context";
import "./Message.css";
import { IoMdClose, IoIosSend, IoMdArrowBack } from "react-icons/io";
import { TiMessage } from "react-icons/ti";
import { GiRingingBell } from "react-icons/gi";
import { BsPaperclip, BsChatText } from "react-icons/bs";
import { FaMicrophone } from "react-icons/fa";

// API Configuration
const branchApiKey = process.env.REACT_APP_API_KEY_AUTH_BRANCH;
const signedUrlApiKey = process.env.REACT_APP_API_KEY_AUTH_SIGNEDURL;
const msgApiKey = process.env.REACT_APP_API_KEY_AUTH_MSG;
const secUrlApiKey = process.env.REACT_APP_API_KEY_AUTH_SECURL;

const MESSAGES_API_URL = `https://${msgApiKey}.execute-api.ap-south-1.amazonaws.com/default/branchMessage`;
const BRANCH_API_URL = `https://${branchApiKey}.execute-api.ap-south-1.amazonaws.com/default/createBranch`;
const S3_PRESIGN_API_URL = `https://${signedUrlApiKey}.execute-api.ap-south-1.amazonaws.com/default/getSignedUrl`;
const SECURED_URL_API = `https://${secUrlApiKey}.execute-api.ap-south-1.amazonaws.com/default/getSecuredImageUrl`;
const POLLING_INTERVAL = 10000;
const MESSAGES_PER_LOAD = 10;

const generateFileKey = (userId, fileName) => {
  const timestamp = Date.now();
  const cleanFileName = fileName.replace(/[^a-zA-Z0-9.]/g, "_");
  return `chat-media/${userId}/${timestamp}_${cleanFileName}`;
};

const getS3KeyFromUrl = (publicUrl) => {
  try {
    const url = new URL(publicUrl);
    return url.pathname.substring(1);
  } catch (e) {
    return null;
  }
};

// --- Secure Media Components ---
const SecureImage = ({ imageUrl, accessToken }) => {
  const [secureSrc, setSecureSrc] = useState(null);

  const [error, setError] = useState(null);
  const s3Key = getS3KeyFromUrl(imageUrl);

  useEffect(() => {
    if (!s3Key || !accessToken) return;
    const fetchSecureUrl = async () => {
      try {
        const res = await fetch(SECURED_URL_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ fileKey: s3Key }),
        });
        if (res.ok) {
          const data = await res.json();
          setSecureSrc(data.securedUrl);
        } else {
          setError("Failed to load image.");
        }
      } catch (e) {
        setError("Network error.");
      }
    };
    fetchSecureUrl();
  }, [s3Key, accessToken]);

  if (error) return <p className="image-status image-error">{error}</p>;
  return (
    <a
      href={secureSrc}
      target="_blank"
      rel="noopener noreferrer"
      className="chat-image-link"
    >
      <img
        src={secureSrc}
        alt="Shared media"
        className="chat-image-preview"
        height={150}
        width={150}
      />
    </a>
  );
};

const SecureAudio = ({ audioUrl, accessToken }) => {
  const [secureSrc, setSecureSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const s3Key = getS3KeyFromUrl(audioUrl);

  useEffect(() => {
    if (!s3Key || !accessToken) return;
    const fetchSecureUrl = async () => {
      try {
        const res = await fetch(SECURED_URL_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ fileKey: s3Key }),
        });
        if (res.ok) {
          const data = await res.json();
          setSecureSrc(data.securedUrl);
        } else {
          setError("Failed to load audio.");
        }
      } catch (e) {
        setError("Network error.");
      } finally {
        setLoading(false);
      }
    };
    fetchSecureUrl();
  }, [s3Key, accessToken]);

  if (loading) return <p className="audio-status">Loading audio...</p>;
  if (error) return <p className="audio-status audio-error">{error}</p>;
  return (
    <div className="chat-audio-player">
      <audio controls src={secureSrc} style={{ width: "250px" }} />
    </div>
  );
};

const MessageTick = ({ isViewed, isPending }) => {
  if (isPending) {
    // Single grey clock-style tick for optimistic "just sent"
    return (
      <svg className="msg-tick" viewBox="0 0 16 11" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 5.5L5 9.5L15 1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  }
  if (isViewed) {
    // Double blue ticks
    return (
      <svg className="msg-tick msg-tick--read" viewBox="0 0 22 11" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 5.5L5 9.5L15 1.5" stroke="#4fc3f7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M7 5.5L11 9.5L21 1.5" stroke="#4fc3f7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  }
  // Double grey ticks (delivered)
  return (
    <svg className="msg-tick" viewBox="0 0 22 11" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1 5.5L5 9.5L15 1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M7 5.5L11 9.5L21 1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
};

const Message = () => {
  const auth = useAuth();
  const accessToken = auth.user?.access_token;
  const currentUserId = auth.user?.profile?.email || null;
  const sessionStartRef = useRef(new Date());
  const isManualLoadingRef = useRef(false);
  // UI & Data State
  const [newMessage, setNewMessage] = useState("");
  const [replyTo, setReplyTo] = useState(null); // Reply State
  const [error, setError] = useState("");
  const [chatVisible, setChatVisible] = useState(false);
  const [uploadStep, setUploadStep] = useState("");

  const [newMessagesByBranch, setNewMessagesByBranch] = useState([]);
  const [lastReadTimestamps, setLastReadTimestamps] = useState(() => {
    try { return JSON.parse(localStorage.getItem("vcap_last_read") || "{}"); }
    catch { return {}; }
  });
  const [currentSenderName, setCurrentSenderName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const [allBranches, setAllBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [allFetchedMessages, setAllFetchedMessages] = useState([]);
  const [isBranchListLoading, setIsBranchListLoading] = useState(false);
  const [searchBranch, setSearchBranch] = useState("");
  // Recording State
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  // Pagination State
  const [messagesLoadedCount, setMessagesLoadedCount] =
    useState(MESSAGES_PER_LOAD);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isOlderLoading, setIsOlderLoading] = useState(false);

  const messagesEndRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const fileInputRef = useRef(null);
  const prevScrollHeightRef = useRef(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleMessagesScroll = () => {
    const el = messagesAreaRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollBtn(distFromBottom > 120);
  };

  const handleReply = (msg) => {
    setReplyTo(msg);
    // Focus the input field
    const input = document.querySelector(
      ".message-input-form input[type='text']",
    );
    if (input) input.focus();
  };

  const cancelReply = () => {
    setReplyTo(null);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const visibleMessages = useMemo(() => {
    if (!selectedBranch || !currentUserId || !currentSenderName) return [];
    const filtered = allFetchedMessages.filter((msg) => {
      const iSentIt =
        msg.senderName === currentSenderName &&
        msg.receiverName === selectedBranch.branchName;
      const theySentIt =
        msg.senderName === selectedBranch.branchName &&
        msg.receiverName === currentSenderName;
      return iSentIt || theySentIt;
    });

    filtered.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    const newVisibleMessages = filtered.slice(-messagesLoadedCount);
    setHasMoreMessages(filtered.length > messagesLoadedCount);
    return newVisibleMessages;
  }, [
    allFetchedMessages,
    selectedBranch,
    currentUserId,
    currentSenderName,
    messagesLoadedCount,
  ]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      setAudioBlob(null);
      setSelectedFile(null);
      mediaRecorderRef.current.ondataavailable = (event) =>
        audioChunksRef.current.push(event.data);
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, {
          type: mediaRecorderRef.current.mimeType,
        });
        setAudioBlob(blob);
        stream.getTracks().forEach((track) => track.stop());
      };
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(
        () => setRecordingTime((prev) => prev + 1),
        1000,
      );
    } catch (err) {
      setError("Microphone access denied.");
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingTimerRef.current);
    }
  };

  const cancelRecording = () => {
    stopRecording();
    setAudioBlob(null);
    setRecordingTime(0);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setSelectedFile(file);
      setAudioBlob(null);
      setError("");
    } else {
      setError("Please select an image file.");
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setAudioBlob(null);
    setUploadStep("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const fetchBranches = useCallback(async () => {
    if (!accessToken || !currentUserId) return;
    setIsBranchListLoading(true);
    try {
      const res = await fetch(BRANCH_API_URL);
      const data = await res.json();
      const branches = data.branches || [];
      const me = branches.find((b) => b.email === currentUserId);
      if (me) setCurrentSenderName(me.branchName);
      else setCurrentSenderName(currentUserId);
      setAllBranches(branches.filter((b) => b.email !== currentUserId));
    } catch (error) {
      setError("Branch fetch failed.");
    } finally {
      setIsBranchListLoading(false);
    }
  }, [accessToken, currentUserId]);

  const msgFailCountRef = useRef(0);
  const msgBackoffUntilRef = useRef(0);

  const fetchMessages = useCallback(async () => {
    if (!accessToken || !currentSenderName || allBranches.length === 0) return;
    if (Date.now() < msgBackoffUntilRef.current) return;

    try {
      const response = await fetch(MESSAGES_API_URL);
      const data = await response.json();
      const allMessages = data.messages || [];

      if (JSON.stringify(allMessages) !== JSON.stringify(allFetchedMessages)) {
        const lastTimestamp =
          allFetchedMessages.length > 0
            ? new Date(
                allFetchedMessages[allFetchedMessages.length - 1].timestamp,
              )
            : sessionStartRef.current;

        const newIncoming = allMessages.filter(
          (msg) =>
            msg.receiverName === currentSenderName &&
            new Date(msg.timestamp) > lastTimestamp &&
            msg.senderName !== selectedBranch?.branchName,
        );

        if (newIncoming.length > 0) {
          const uniqueSenders = [
            ...new Set(newIncoming.map((m) => m.senderName)),
          ];
          const branchesToNotify = allBranches.filter((b) =>
            uniqueSenders.includes(b.branchName),
          );
          setNewMessagesByBranch((prev) => {
            const existing = new Set(prev.map((p) => p.email));
            return [
              ...prev,
              ...branchesToNotify.filter((b) => !existing.has(b.email)),
            ];
          });
        }
        setAllFetchedMessages(allMessages);
      }
      msgFailCountRef.current = 0;
    } catch (err) {
      msgFailCountRef.current += 1;
      if (msgFailCountRef.current === 1) {
        console.warn("Message API unreachable — will retry with backoff.");
      }
      // Exponential backoff: 30s → 60s → 120s, capped at 2 min
      const backoffMs = Math.min(30000 * Math.pow(2, msgFailCountRef.current - 1), 120000);
      msgBackoffUntilRef.current = Date.now() + backoffMs;
    }
  }, [
    accessToken,
    currentSenderName,
    allFetchedMessages,
    allBranches,
    selectedBranch,
  ]);

  const markMessagesAsRead = useCallback(async (messages) => {
    if (!accessToken || !selectedBranch) return;
    const unread = messages.filter(
      (m) => m.senderName === selectedBranch.branchName && !m.isViewed
    );
    if (unread.length === 0) return;
    const ids = unread.map((m) => m.messageId);
    try {
      await fetch(MESSAGES_API_URL, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ messageIds: ids }),
      });
      // Optimistically update local state
      setAllFetchedMessages((prev) =>
        prev.map((m) => (ids.includes(m.messageId) ? { ...m, isViewed: true } : m))
      );
    } catch {
      // Silent — read receipt is non-critical
    }
  }, [accessToken, selectedBranch]);

  useEffect(() => {
    if (visibleMessages.length > 0) markMessagesAsRead(visibleMessages);
  }, [visibleMessages, markMessagesAsRead]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  useEffect(() => {
    const id = setInterval(fetchMessages, POLLING_INTERVAL);
    return () => clearInterval(id);
  }, [fetchMessages]);

  useEffect(() => {
    if (chatVisible) scrollToBottom();
  }, [visibleMessages, chatVisible]);
  // 1. Add a ref to store all message elements
  // const messageRefs = useRef({});

  // 2. Function to scroll to the original message
  const scrollToMessage = async (targetId) => {
    if (!targetId) return;

    let targetElement = document.getElementById(`msg-${targetId}`);

    // If not found, try loading more messages (if possible)
    if (!targetElement && hasMoreMessages) {
      alert("Message is older. Loading more chat history...");

      // Increase the load count
      setMessagesLoadedCount((prev) => prev + 20);

      // Wait for React to re-render the new messages
      setTimeout(() => {
        const retryElement = document.getElementById(`msg-${targetId}`);
        if (retryElement) {
          retryElement.scrollIntoView({ behavior: "smooth", block: "center" });
          retryElement.classList.add("jump-highlight");
        } else {
          alert("Message still not found. Try clicking 'Load Older' manually.");
        }
      }, 1000); // Give React 1 second to render
      return;
    }

    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
      targetElement.classList.add("jump-highlight");
      setTimeout(() => targetElement.classList.remove("jump-highlight"), 2000);
    }
  };
  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    const text = newMessage.trim();
    const branch = selectedBranch;
    if (!branch) {
      setError("Select a branch.");
      return;
    }

    setIsUploading(true);
    setError("");
    setUploadStep("Syncing...");

    try {
      let finalFileUrl = null;
      let contentType = null;
      const media = selectedFile || audioBlob;
      if (media) {
        contentType = media.type || (audioBlob ? "audio/ogg" : "image/jpeg");
        const key = generateFileKey(
          currentUserId,
          audioBlob ? `voice_${Date.now()}.ogg` : selectedFile.name,
        );
        const preRes = await fetch(S3_PRESIGN_API_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ fileKey: key, contentType }),
        });
        const { signedUrl, publicUrl } = await preRes.json();
        await fetch(signedUrl, {
          method: "PUT",
          body: media,
          headers: { "Content-Type": contentType },
        });
        finalFileUrl = publicUrl;
      }

      setUploadStep("Finalizing...");

      const body = {
        text,
        clientSenderName: currentSenderName,
        receiverName: branch.branchName,
        imageUrl: contentType?.startsWith("image/") ? finalFileUrl : null,
        audioUrl: contentType?.startsWith("audio/") ? finalFileUrl : null,
        timestamp: new Date().toISOString(),
        replyToId: replyTo?.messageId || null,
        replyText:
          replyTo?.text ||
          (replyTo?.imageUrl ? "Image" : replyTo?.audioUrl ? "Voice" : null),
        replySender: replyTo?.senderName || null,
      };

      const res = await fetch(MESSAGES_API_URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error("Post Failed.");
      setUploadStep("Success!");
      setNewMessage("");
      setReplyTo(null);
      handleRemoveFile();
      setTimeout(() => setUploadStep(""), 2000);
    } catch (e) {
      setError(e.message);
      setUploadStep("Failed");
    } finally {
      setIsUploading(false);
    }
  };

  const loadOlderMessages = () => {
    const el = messagesAreaRef.current;
    if (el) prevScrollHeightRef.current = el.scrollHeight;
    isManualLoadingRef.current = true;
    setIsOlderLoading(true);
    setMessagesLoadedCount((prev) => prev + MESSAGES_PER_LOAD);
    setTimeout(() => {
      setIsOlderLoading(false);
      isManualLoadingRef.current = false;
    }, 500);
  };

  // Restore scroll position after older messages are prepended
  useEffect(() => {
    const el = messagesAreaRef.current;
    if (el && prevScrollHeightRef.current !== null) {
      el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
      prevScrollHeightRef.current = null;
    }
  }, [visibleMessages]);

  const markBranchAsRead = (branch) => {
    const now = new Date().toISOString();
    setLastReadTimestamps((prev) => {
      const updated = { ...prev, [branch.branchName]: now };
      localStorage.setItem("vcap_last_read", JSON.stringify(updated));
      return updated;
    });
    setNewMessagesByBranch((prev) => prev.filter((b) => b.email !== branch.email));
  };

  const handleNotificationClick = (branch) => {
    setSelectedBranch(branch);
    setChatVisible(true);
    markBranchAsRead(branch);
  };
  // Add this hook to calculate the sorted branch list
  const sortedBranchList = useMemo(() => {
    if (!currentSenderName) return [];
    return allBranches
      .map((branch) => {
        // Find the last message between you and this branch
        const lastMsg = allFetchedMessages
          .filter(
            (m) =>
              (m.senderName === branch.branchName &&
                m.receiverName === currentSenderName) ||
              (m.senderName === currentSenderName &&
                m.receiverName === branch.branchName),
          )
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];

        return { ...branch, lastMessage: lastMsg };
      })
      .sort((a, b) => {
        // Sort so branches with newer messages are at index 0
        const timeA = a.lastMessage
          ? new Date(a.lastMessage.timestamp).getTime()
          : 0;
        const timeB = b.lastMessage
          ? new Date(b.lastMessage.timestamp).getTime()
          : 0;
        return timeB - timeA;
      });
  }, [allBranches, allFetchedMessages, currentSenderName]);
  const unreadCounts = useMemo(() => {
    if (!currentSenderName) return {};
    const counts = {};
    allFetchedMessages.forEach((msg) => {
      if (msg.receiverName !== currentSenderName) return;
      // isViewed is persisted in DB — reliable across reloads and logins
      if (msg.isViewed) return;
      // Fall back to localStorage timestamp for messages not yet marked in DB
      const sender = msg.senderName;
      const lastRead = lastReadTimestamps[sender];
      if (!lastRead || new Date(msg.timestamp) > new Date(lastRead)) {
        counts[sender] = (counts[sender] || 0) + 1;
      }
    });
    return counts;
  }, [allFetchedMessages, currentSenderName, lastReadTimestamps]);

  const [searchedBranches, setSearchedBranches] = useState([]);
  useEffect(() => {
    if (searchBranch.trim() === "") return;
    const filtered = sortedBranchList.filter((b) =>
      b.branchName.toLowerCase().includes(searchBranch.toLowerCase()),
    );
    setSearchedBranches(filtered);
  }, [sortedBranchList, searchBranch]);
  return (
    <div className="branch-chat-wrapper">
      {/* Notification Stack using GiRingingBell */}
      <div className="notification-stack-container">
        {newMessagesByBranch.map((b) => (
          <div
            key={b.email}
            className="new-message-notification"
            onClick={() => {
              setSelectedBranch(b);
              setChatVisible(true);
              setNewMessagesByBranch((prev) =>
                prev.filter((item) => item.email !== b.email),
              );
              handleNotificationClick(b);
            }}
          >
            <GiRingingBell className="notification-icon" />
            <span>
              New message from <b>{b.branchName}</b>
            </span>
          </div>
        ))}
      </div>

      {!chatVisible && (
        <button
          className={`chat-toggle-button ${newMessagesByBranch.length > 0 ? "notification-active" : ""}`}
          onClick={() => setChatVisible(true)}
        >
          <TiMessage size={24} /> Chat
          {newMessagesByBranch.length > 0 && (
            <span className="notification-badge">
              {newMessagesByBranch.length}
            </span>
          )}
        </button>
      )}

      <div
        className={`branch-chat-container ${chatVisible ? "visible" : "hidden"}`}
      >
        <header className="chat-header">
          <h1>
            <TiMessage /> VCAP Messenger
          </h1>
          <button onClick={() => {setChatVisible(false); setSelectedBranch(null);}} className="close-chat-btn">
            <IoMdClose size={24} />
          </button>
        </header>

        <div className="chat-main-content dual-pane-mode">
          {/* WHATSAPP STYLE SIDEBAR */}
          <div
            className={`chat-sidebar ${selectedBranch ? "mobile-hide" : ""}`}
          >
            <div className="sidebar-header-row">
              <h3 className="Chat-h1">
                Chat To: <input type="text" onChange={(e)=> setSearchBranch(e.target.value)} style={{width:"130px"}} placeholder="Search Branch"/>
                {isBranchListLoading && (
                  <span className="small-loader">...</span>
                )}
              </h3>
            </div>
            <div className="branch-list-container">
              {(searchBranch.trim() ? searchedBranches : sortedBranchList).map((branch) => {
                const unread = unreadCounts[branch.branchName] || 0;
                return (
                  <div
                    key={branch.email}
                    className={`branch-item ${selectedBranch?.email === branch.email ? "active" : ""} ${unread > 0 ? "unread" : ""}`}
                    onClick={() => {
                      setSelectedBranch(branch);
                      markBranchAsRead(branch);
                    }}
                  >
                    <div className="branch-avatar">
                      {branch.branchName.charAt(0).toUpperCase()}
                    </div>
                    <div className="branch-info">
                      <div className="branch-top-row">
                        <span className="branch-name">{branch.branchName}</span>
                        <span className="last-msg-time">
                          {branch.lastMessage && new Date(branch.lastMessage.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                      <div className="branch-bottom-row">
                        <p className="last-msg-preview">
                          {branch.lastMessage?.text ||
                            (branch.lastMessage?.imageUrl ? "📷 Photo"
                              : branch.lastMessage?.audioUrl ? "🎤 Voice"
                              : "No messages yet")}
                        </p>
                        {unread > 0 && (
                          <span className="unread-count-badge">{unread}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CHAT AREA */}
          <div
            className={`selected-chat-area ${!selectedBranch ? "mobile-hide" : "full-width"}`}
          >
            {selectedBranch ? (
              <>
                <header className="chat-partner-header">
                  <button
                    className="back-btn"
                    onClick={() => setSelectedBranch(null)}
                  >
                    <IoMdArrowBack />
                  </button>
                  <BsChatText />
                  <span className="partner-name">
                    {selectedBranch.branchName}
                  </span>
                </header>

                {error && <div className="chat-error-banner">{error}</div>}

                <div className="messages-area" ref={messagesAreaRef} onScroll={handleMessagesScroll}>
                  {hasMoreMessages && (
                    <button
                      onClick={loadOlderMessages}
                      className="load-more-button"
                      disabled={isOlderLoading}
                    >
                      {isOlderLoading ? "Loading..." : "Load Older"}
                    </button>
                  )}
                  {visibleMessages.map((msg, i) => (
                    <div
                      key={msg.messageId || i}
                      id={`msg-${msg.messageId}`}
                      className={`message-row ${msg.senderName === currentSenderName ? "my-message-row" : "other-message-row"}`}
                    >
                      <div
                        className={`message-bubble ${msg.senderName === currentSenderName ? "my-message" : "other-message"}`}
                      >
                        {(msg.replyToId || msg.replyText) && (
                          <div
                            className="message-quote-box"
                            onClick={(e) => {
                              e.stopPropagation();
                              scrollToMessage(msg.replyToId || msg.replytoid);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <span className="quote-sender">
                              {msg.replySender || "User"}
                            </span>
                            <p className="quote-text">
                              {msg.replyText || "Media Attachment"}
                            </p>
                          </div>
                        )}

                        {msg.senderName !== currentSenderName && (
                          <p className="message-sender">{msg.senderName}</p>
                        )}

                        <div className="message-body">
                          {msg.imageUrl && (
                            <SecureImage
                              imageUrl={msg.imageUrl}
                              accessToken={accessToken}
                            />
                          )}
                          {msg.audioUrl && (
                            <SecureAudio
                              audioUrl={msg.audioUrl}
                              accessToken={accessToken}
                            />
                          )}
                          {msg.text && (
                            <p className="text-content">{msg.text}</p>
                          )}
                        </div>

                        <div className="message-metadata">
                          <button
                            className="reply-trigger-icon"
                            onClick={() => handleReply(msg)}
                          >
                            Reply
                          </button>
                          <span className="message-time">
                            {new Date(msg.timestamp).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                          {msg.senderName === currentSenderName && (
                            <MessageTick isViewed={msg.isViewed} isPending={msg._pending} />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {showScrollBtn && (
                  <button
                    className="scroll-to-bottom-btn"
                    onClick={() => {
                      scrollToBottom();
                      setShowScrollBtn(false);
                    }}
                    title="Scroll to latest"
                  >
                    ↓
                  </button>
                )}

                <form
                  onSubmit={handleSendMessage}
                  className="message-input-form"
                >
                  {isUploading && (
                    <div className="upload-progress-container">
                      <div className="progress-label">{uploadStep}</div>
                      <div className="progress-bar-bg">
                        <div
                          className="progress-bar-fill"
                          style={{
                            width: uploadStep === "Success!" ? "100%" : "50%",
                          }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {replyTo && (
                    <div className="reply-preview-container">
                      <div className="reply-content">
                        <span className="reply-sender">
                          {replyTo.senderName}
                        </span>
                        <p className="reply-text">
                          {replyTo.text || "Media Attachment"}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={cancelReply}
                        className="cancel-reply"
                      >
                        <IoMdClose size={18} />
                      </button>
                    </div>
                  )}

                  {isRecording && (
                    <div className="recording-status-bar">
                      <span className="recording-timer">
                        ● {formatTime(recordingTime)}
                      </span>
                      <button
                        type="button"
                        onClick={cancelRecording}
                        className="cancel-record"
                      >
                        &times;
                      </button>
                    </div>
                  )}

                  <div className="input-group">
                    <input
                      type="file"
                      ref={fileInputRef}
                      hidden
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current.click()}
                      disabled={isRecording}
                    >
                      <BsPaperclip />
                    </button>
                    <button
                      type="button"
                      onClick={isRecording ? stopRecording : startRecording}
                      className={isRecording ? "is-recording" : ""}
                    >
                      <FaMicrophone />
                    </button>
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder={
                        isRecording ? "Recording..." : "Type here..."
                      }
                      disabled={isUploading || isRecording}
                    />
                    <button
                      type="submit"
                      disabled={
                        isUploading ||
                        isRecording ||
                        (!newMessage && !selectedFile && !audioBlob)
                      }
                    >
                      <IoIosSend size={24} />
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="no-chat-selected">
                <TiMessage size={60} />
                <p>Select a branch to start communicating</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Message;
